import { useEffect, useRef } from 'react';

/**
 * After `debounceMs` of stability, runs `validateForm()`; if valid, calls `save()` (no validation inside `save`).
 * Skips while `!dirty`, `isSubmitting`, or `!enabled`.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {boolean} opts.dirty
 * @param {boolean} opts.isSubmitting
 * @param {unknown} opts.valuesWatch - any Formik values object; resets the debounce timer when it changes
 * @param {() => Promise<object|undefined>} opts.validateForm
 * @param {() => Promise<void>} opts.save
 * @param {number} [opts.debounceMs]
 */
export function useDebouncedValidDraftSave({
  enabled,
  dirty,
  isSubmitting,
  valuesWatch,
  validateForm,
  save,
  debounceMs = 2000,
}) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    clearTimeout(timerRef.current);
    if (!dirty || isSubmitting) return undefined;

    timerRef.current = setTimeout(async () => {
      try {
        const errs = await validateForm();
        if (errs && Object.keys(errs).length > 0) return;
        await save();
      } catch {
        /* autosave is best-effort */
      }
    }, debounceMs);

    return () => clearTimeout(timerRef.current);
  }, [
    enabled,
    dirty,
    isSubmitting,
    valuesWatch,
    validateForm,
    save,
    debounceMs,
  ]);
}
